import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { formatDateTime, labelText } from "../lib/format";
import { Loading, ErrorState } from "../components/ui";
import { VisitModal } from "../components/VisitModal";
import { DoctorCardModal } from "../components/DoctorCard";
import type { AppointmentView, DoctorSummary } from "@forcebase-ai/schema/patient";

// Non-clinical membership copy (no health data). Keyed by tier. Price is the public plan price.
const TIER_INFO: Record<string, { name: string; desc: string; price: string }> = {
  basic: { name: "Basic", desc: "On-demand telehealth visits with unlimited secure messaging.", price: "$50" },
  premium: { name: "Premium", desc: "Priority telehealth visits, unlimited messaging, and your care team.", price: "$100" },
  fighter: { name: "Fighter", desc: "Invitation-only membership with concierge access.", price: "$0" },
};

function isOpen(a: AppointmentView): boolean {
  return a.status !== "cancelled" && a.status !== "completed";
}

function nextUpcoming(appts: AppointmentView[]): AppointmentView | null {
  const now = Date.now();
  return (
    appts
      .filter(isOpen)
      .filter((a) => new Date(a.scheduled_at).getTime() > now - 60 * 60 * 1000)
      .sort((a, b) => +new Date(a.scheduled_at) - +new Date(b.scheduled_at))[0] ?? null
  );
}

const dayNum = (iso: string) => new Date(iso).getDate();
const monthAbbr = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: "short" }).toUpperCase();
const isToday = (iso: string) => new Date(iso).toDateString() === new Date().toDateString();

function visitStatusClass(status: string): string {
  if (status === "completed") return "completed";
  if (status === "cancelled") return "canceled";
  return "upcoming";
}

const Ic = {
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  video: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" /></svg>,
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  book: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  aria: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z" /></svg>,
  records: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
  messages: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22 6 12 13 2 6" /></svg>,
};

export function Dashboard() {
  const api = useApi();
  const me = useAsync(() => api.me(), []);
  const visits = useAsync(() => api.listAppointments(), []);
  const billing = useAsync(() => api.billing(), []);
  const doctors = useAsync(() => api.listDoctors(), []);
  const [modalAppt, setModalAppt] = useState<AppointmentView | null>(null);
  const [modalView, setModalView] = useState<"detail" | "reschedule">("detail");
  const [cardDoctorId, setCardDoctorId] = useState<string | null>(null);
  const openVisit = (a: AppointmentView, view: "detail" | "reschedule" = "detail") => {
    setModalAppt(a);
    setModalView(view);
  };

  if (me.loading || visits.loading) return <Loading />;
  if (me.error) return <ErrorState message={me.error} />;
  if (visits.error) return <ErrorState message={visits.error} />;

  const first = me.data?.first_name ?? "there";
  const all = visits.data ?? [];
  const next = nextUpcoming(all);
  const byId = new Map<string, DoctorSummary>((doctors.data ?? []).map((d) => [d.id, d]));
  const nextDoctor = next ? byId.get(next.doctor_id) : undefined;
  const nextDoctorName = nextDoctor ? `Dr. ${nextDoctor.first_name} ${nextDoctor.last_name}` : null;
  const recent = all
    .filter((a) => a.status === "completed" || a.status === "cancelled")
    .sort((a, b) => +new Date(b.scheduled_at) - +new Date(a.scheduled_at))
    .slice(0, 4);
  const upcomingCount = all.filter(isOpen).length;
  const completedCount = all.filter((a) => a.status === "completed").length;
  const tier = billing.data?.tier ?? me.data?.tier ?? null;
  const plan = tier ? TIER_INFO[tier] : null;

  return (
    <>
      <div className="app-page-head">
        <h1>Hello, <em>{first}.</em></h1>
        <p>Here&rsquo;s what&rsquo;s happening with your care.</p>
      </div>

      <div className="dash-grid">
        <div>
          {/* Upcoming visit hero */}
          {next ? (
            <div className="upcoming-visit">
              <span className="upcoming-visit-tag">Upcoming{isToday(next.scheduled_at) ? " · Today" : ""}</span>
              <h3>{formatDateTime(next.scheduled_at)}{nextDoctorName && (
                <> with{" "}
                  <button type="button" className="upcoming-visit-doctor" onClick={() => setCardDoctorId(next.doctor_id)}>
                    {nextDoctorName}
                  </button>
                </>
              )}</h3>
              <div className="upcoming-visit-meta">
                <div>{Ic.clock}{next.duration_minutes}-min telehealth visit</div>
                <div>{Ic.video}Video call</div>
                <div>{Ic.info}{labelText(next.label)}</div>
              </div>
              <div className="upcoming-visit-actions">
                <button className="btn btn-primary" onClick={() => openVisit(next, "detail")}>View visit</button>
                <button className="btn btn-ghost" onClick={() => openVisit(next, "reschedule")}>Reschedule</button>
              </div>
            </div>
          ) : (
            <div className="upcoming-visit">
              <span className="upcoming-visit-tag">No upcoming visits</span>
              <h3>You&rsquo;re all <em>caught up.</em></h3>
              <div className="upcoming-visit-meta"><div>{Ic.info}Book a visit whenever you need care.</div></div>
              <div className="upcoming-visit-actions">
                <Link className="btn btn-primary" to="/visits/book">Book a visit</Link>
              </div>
            </div>
          )}

          {/* Quick tiles */}
          <div className="quick-tiles">
            <Link to="/visits/book" className="quick-tile">
              <span className="quick-tile-ic">{Ic.book}</span>
              <div><h4>Book a visit</h4><p>Same-day appointments</p></div>
            </Link>
            <Link to="/concierge" className="quick-tile">
              <span className="quick-tile-ic">{Ic.aria}</span>
              <div><h4>Ask Aria</h4><p>Your AI care guide</p></div>
            </Link>
            <Link to="/care" className="quick-tile">
              <span className="quick-tile-ic ink">{Ic.records}</span>
              <div><h4>Records</h4><p>Visits &amp; prescriptions</p></div>
            </Link>
            <Link to="/messages" className="quick-tile">
              <span className="quick-tile-ic ink">{Ic.messages}</span>
              <div><h4>Messages</h4><p>Secure care-team chat</p></div>
            </Link>
          </div>

          {/* Recent activity */}
          <div className="app-card">
            <div className="app-card-head">
              <h3>Recent activity</h3>
              <Link to="/visits">View all →</Link>
            </div>
            {recent.length ? (
              <div className="visit-list">
                {recent.map((a) => (
                  <button type="button" className="visit-row" onClick={() => openVisit(a, "detail")} key={a.id}>
                    <div className="visit-date">
                      <div className="day">{dayNum(a.scheduled_at)}</div>
                      <div className="month">{monthAbbr(a.scheduled_at)}</div>
                    </div>
                    <div className="visit-info">
                      <div className="visit-doctor">{labelText(a.label)}</div>
                      <div className="visit-detail-text"><span>{formatDateTime(a.scheduled_at)}</span></div>
                    </div>
                    <span className={`visit-status ${visitStatusClass(a.status)}`}>{a.status}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="pt-empty">No past visits yet.</div>
            )}
          </div>
        </div>

        <div>
          {/* Plan summary */}
          {plan && (
            <div className="plan-summary-card">
              <h4>Your plan</h4>
              <div className="plan-summary-card-name">{plan.name}</div>
              <p className="plan-summary-card-desc">{plan.desc}</p>
              <div className="plan-summary-card-price">
                <span className="amt">{plan.price}</span>
                <span className="per">/month</span>
              </div>
              <div className="plan-summary-card-actions">
                <Link className="btn" to="/account?tab=plan">Manage plan</Link>
              </div>
            </div>
          )}

          {/* At a glance */}
          <div className="app-card" style={{ marginTop: 20 }}>
            <div className="app-card-head"><h3>At a glance</h3></div>
            <div className="dash-stat-row">
              <div className="dash-stat">
                <div className="dash-stat-label">Upcoming</div>
                <div className="dash-stat-value"><em>{upcomingCount}</em></div>
                <div className="dash-stat-sub">visits scheduled</div>
              </div>
              <div className="dash-stat">
                <div className="dash-stat-label">Completed</div>
                <div className="dash-stat-value"><em>{completedCount}</em></div>
                <div className="dash-stat-sub">all-time</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalAppt && (
        <VisitModal
          appt={modalAppt}
          doctor={byId.get(modalAppt.doctor_id)}
          initialView={modalView}
          onClose={() => setModalAppt(null)}
          onChanged={() => visits.reload()}
        />
      )}

      {/* View-only doctor card from the upcoming-visit name (favorite allowed, no "choose"). */}
      {cardDoctorId && (
        <DoctorCardModal doctorId={cardDoctorId} onClose={() => setCardDoctorId(null)} />
      )}
    </>
  );
}
